<template>
    <a-modal :open="open" :footer="null" :title="null" :width="isForm ? '660px' : '560px'" @cancel="onCancel">
        <a-form layout="vertical" class="dm">
            <div class="dm_header">
                <h2 class="dm_title">{{ isForm ? form?.title : 'Загрузка документа' }}</h2>
                <p class="dm_subtitle">
                    {{ isForm ? 'Проверьте данные заявителя и подпишите документ' : document?.name }}
                </p>
            </div>

            <!-- Подписание уже открыто: даём вернуться в ту же сессию или начать заново.
                 Без этого брошенное подписание навсегда закрепляло за документом мёртвую ссылку. -->
            <template v-if="isForm && openLink">
                <a-alert type="info" show-icon class="dm_alert">
                    <template #message>
                        <span class="dm_alert_text">
                            Документ уже отправлен на подписание. Продолжите в signature.kz
                            или создайте новую ссылку, если прежняя больше не работает.
                        </span>
                    </template>
                </a-alert>

                <div class="dm_footer">
                    <a-button class="dm_btn" :disabled="loading" @click="emit('abandon', document)">
                        Создать новое заявление
                    </a-button>
                    <a-button type="primary" class="dm_btn" :href="openLink" target="_blank">
                        Продолжить подписание
                    </a-button>
                </div>
            </template>

            <template v-else-if="isForm">
                <div class="dm_section">
                    <p class="dm_section_title">ДАННЫЕ ЗАЯВИТЕЛЯ</p>
                    <div class="dm_grid">
                        <a-form-item class="dm_item" label="ИИН" :validate-status="iinError"
                            :help="iinError || ''">
                            <a-input v-model:value="iin" placeholder="Укажите ИИН" v-maska="'############'"
                                :class="iinError ? 'error' : ''">
                                <template v-if="isLookingUp" #suffix>
                                    <loading-outlined />
                                </template>
                            </a-input>
                        </a-form-item>

                        <a-form-item class="dm_item" label="ФИО Заявителя" :validate-status="fullNameError"
                            :help="fullNameError || ''">
                            <a-input v-model:value="fullName" :disabled="isLookingUp" placeholder="Фамилия Имя Отчество"
                                :class="fullNameError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Страна" :validate-status="countryError"
                            :help="countryError || ''">
                            <a-input v-model:value="country" placeholder="Укажите страну"
                                :class="countryError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Адрес проживания" :validate-status="addressError"
                            :help="addressError || ''">
                            <a-input v-model:value="address" :disabled="isLookingUp" placeholder="Введите адрес"
                                :class="addressError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Номер телефона" :validate-status="phoneError"
                            :help="phoneError || ''">
                            <a-input v-model:value="phone" :disabled="isLookingUp" placeholder="+7 (___) ___ __ __"
                                v-maska="'+7 (###) ### ## ##'" :class="phoneError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Email" :validate-status="emailError"
                            :help="emailError || ''">
                            <a-input v-model:value="email" :disabled="isLookingUp" placeholder="Введите email"
                                :class="emailError ? 'error' : ''" />
                        </a-form-item>
                    </div>
                </div>

                <div class="dm_section">
                    <p class="dm_section_title">ДАННЫЕ ПО ТРАНСПОРТНОМУ СРЕДСТВУ</p>
                    <div class="dm_grid">
                        <a-form-item class="dm_item" label="Марка и модель" :validate-status="markModelError"
                            :help="markModelError || ''">
                            <a-input v-model:value="markModel" placeholder="Например, KIA SOUL"
                                :class="markModelError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Гос. номер" :validate-status="plateNumberError"
                            :help="plateNumberError || ''">
                            <a-input v-model:value="plateNumber" placeholder="Гос. рег. знак"
                                :class="plateNumberError ? 'error' : ''" />
                        </a-form-item>

                        <template v-if="hasRecipient">
                            <a-form-item class="dm_item" label="Дата события" :validate-status="incidentDateError"
                                :help="incidentDateError || ''">
                                <a-input v-model:value="incidentDate" placeholder="ГГГГ-ММ-ДД"
                                    v-maska="'####-##-##'" :class="incidentDateError ? 'error' : ''" />
                            </a-form-item>

                            <a-form-item class="dm_item" label="Время события" :validate-status="incidentTimeError"
                                :help="incidentTimeError || ''">
                                <a-input v-model:value="incidentTime" placeholder="ЧЧ:ММ" v-maska="'##:##'"
                                    :class="incidentTimeError ? 'error' : ''" />
                            </a-form-item>
                        </template>
                    </div>
                </div>

                <div v-if="hasRecipient" class="dm_section">
                    <p class="dm_section_title">ДАННЫЕ ВЫГОДОПРИОБРЕТАТЕЛЯ</p>
                    <div class="dm_grid">
                        <a-form-item class="dm_item" label="ИИН получателя"
                            :validate-status="recipientIinError" :help="recipientIinError || ''">
                            <a-input v-model:value="recipientIin" placeholder="Укажите ИИН"
                                v-maska="'############'" :class="recipientIinError ? 'error' : ''">
                                <template v-if="isLookingUpRecipient" #suffix>
                                    <loading-outlined />
                                </template>
                            </a-input>
                        </a-form-item>

                        <a-form-item class="dm_item" label="Получатель ФИО" :validate-status="recipientNameError"
                            :help="recipientNameError || ''">
                            <a-input v-model:value="recipientName" :disabled="isLookingUpRecipient"
                                placeholder="Фамилия Имя Отчество" :class="recipientNameError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="ИИК получателя" :validate-status="recipientIbanError"
                            :help="recipientIbanError || ''">
                            <a-input v-model:value="recipientIban" placeholder="KZ..."
                                :class="recipientIbanError ? 'error' : ''" />
                        </a-form-item>

                        <a-form-item class="dm_item" label="Банк получателя" :validate-status="recipientBankIdError"
                            :help="recipientBankIdError || ''">
                            <a-select v-model:value="recipientBankId" placeholder="Выберите банк" :options="banks"
                                show-search option-filter-prop="label"
                                :class="recipientBankIdError ? 'error' : ''"
                                :get-popup-container="triggerNode => triggerNode.parentNode" />
                        </a-form-item>
                    </div>
                </div>

                <a-alert type="info" show-icon class="dm_alert">
                    <template #message>
                        <span class="dm_alert_text">
                            ВНИМАНИЕ! После заполнения вы будете перенаправлены на
                            <a href="https://signature.kz" target="_blank" rel="noopener">signature.kz</a>
                            для подписания документа с помощью ЭЦП
                        </span>
                    </template>
                </a-alert>
            </template>

            <template v-else>
                <a-form-item class="dm_item" :validate-status="filesError" :help="filesError || ''">
                    <a-upload-dragger :file-list="files || []" :multiple="false" :accept="ACCEPTED_EXTENSIONS"
                        :before-upload="onBeforeUpload" @remove="onRemoveFile" class="dm_dragger">
                        <p class="dm_dragger_icon">
                            <inbox-outlined />
                        </p>
                        <p class="dm_dragger_title">Перетащите файл сюда или нажмите для выбора</p>
                        <p class="dm_dragger_hint">
                            Допустимые форматы: PDF, DOC, DOCX, JPG, PNG. До {{ MAX_FILE_SIZE_MB }} МБ
                        </p>
                    </a-upload-dragger>
                </a-form-item>
            </template>

            <div v-if="!(isForm && openLink)" class="dm_footer">
                <a-button class="dm_btn" :disabled="loading" @click="onCancel">Отмена</a-button>
                <a-button type="primary" class="dm_btn" :loading="loading" @click="onSubmit">
                    {{ isForm ? 'Подписать через Signature.kz' : 'Загрузить' }}
                </a-button>
            </div>
        </a-form>
    </a-modal>

    <!-- Тот же сценарий, что и в заявлении о ДТП: сначала спрашиваем, заводить ли
         карточку, затем открываем форму контрагента. -->
    <no-data-modal :visible="isNoDataOpen" :on-close="closeNoData" :on-create="openResidentForm" />

    <resident-modal :open="isResidentOpen" :iin="residentIin" @close="closeResident"
        @created="onResidentCreated" />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Alert, Button, DatePicker, Form, FormItem, Input, Modal, Select, Upload } from 'ant-design-vue';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import { useField, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import axios from 'axios';
import { debounce } from '@/views/request-excuses/helpers';
import ResidentModal from './resident-modal.vue';
import NoDataModal from '@/views/request-excuses/components/no-data-modal/no-data-modal.vue';
import { useToast } from 'vue-toastification';
import {
    ACCEPTED_EXTENSIONS,
    MAX_FILE_SIZE_MB,
    STATEMENT_FORMS,
    UPLOAD_SCHEME,
} from '../scheme';

const AModal = Modal;
const AButton = Button;
const AInput = Input;
const ASelect = Select;
const AAlert = Alert;
const AForm = Form;
const AFormItem = FormItem;
const AUploadDragger = Upload.Dragger;

const props = defineProps({
    open: { type: Boolean, default: false },
    // 'form' — заявление с подписанием, 'upload' — прикрепление файла.
    type: { type: String, default: 'upload' },
    document: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    companyId: { type: [Number, String], default: null },
    // Ответ GET .../form: страна, ТС, дата события и ссылка на открытую сессию.
    prefill: { type: Object, default: null },
});

const emit = defineEmits(['close', 'submit', 'abandon']);
const toaster = useToast();

const isForm = computed(() => props.type === 'form');
const form = computed(() => STATEMENT_FORMS[props.document?.doc_isn] ?? null);
const hasRecipient = computed(() => form.value?.hasRecipient === true);
const openLink = computed(() => props.prefill?.signature_link ?? null);

const { handleSubmit, resetForm } = useForm({
    validationSchema: computed(
        () => toTypedSchema(isForm.value && form.value ? form.value.scheme : UPLOAD_SCHEME),
    ),
});

const { value: fullName, errorMessage: fullNameError } = useField('full_name');
const { value: iin, errorMessage: iinError } = useField('iin');
const { value: country, errorMessage: countryError } = useField('country');
const { value: address, errorMessage: addressError } = useField('address');
const { value: phone, errorMessage: phoneError } = useField('phone');
const { value: email, errorMessage: emailError } = useField('email');

const { value: markModel, errorMessage: markModelError } = useField('vehicle_mark_model');
const { value: plateNumber, errorMessage: plateNumberError } = useField('vehicle_plate_number');

const { value: incidentDate, errorMessage: incidentDateError } = useField('incident_date');
const { value: incidentTime, errorMessage: incidentTimeError } = useField('incident_time');

const { value: recipientName, errorMessage: recipientNameError } = useField('recipient_name');
const { value: recipientIin, errorMessage: recipientIinError } = useField('recipient_iin');
const { value: recipientIban, errorMessage: recipientIbanError } = useField('recipient_iban');
const { value: recipientBankId, errorMessage: recipientBankIdError } = useField('recipient_bank_id');

const { value: files, errorMessage: filesError } = useField('files');

const banks = ref([]);
const isLookingUp = ref(false);

const loadBanks = async () => {
    if (banks.value.length) return;
    try {
        const res = await axios.get('/V1/my-insurance-payments/catalogs/banks', {
            params: { company_id: props.companyId },
        });
        banks.value = (res.data || []).map(bank => ({ label: bank.name, value: bank.id }));
    } catch {
        toaster.error('Не удалось загрузить справочник банков', { position: 'top-right' });
    }
};

// Карточки в КИАС нет — заводим её прямо отсюда, формой с /create-contragent:
// уход на страницу потерял бы уже заполненное заявление.
const isNoDataOpen = ref(false);
const isResidentOpen = ref(false);
const residentIin = ref('');
const residentTarget = ref('applicant');

// КИАС видит новую карточку не сразу: если только что созданный ИИН всё ещё не
// находится, второе окно создания дало бы дубль — просим повторить поиск.
const createdIins = new Set();

const promptResident = (target, value) => {
    if (createdIins.has(String(value))) {
        toaster.warning('Контрагент создан, но пока не виден в КИАС — повторите поиск через минуту', {
            position: 'top-right',
        });
        return;
    }

    toaster.warning(
        target === 'recipient'
            ? 'Получатель с таким ИИН не найден в КИАС — создайте его'
            : 'Контрагент с таким ИИН не найден в КИАС — создайте его',
        { position: 'top-right' },
    );

    residentTarget.value = target;
    residentIin.value = String(value);
    isNoDataOpen.value = true;
};

const closeNoData = () => {
    isNoDataOpen.value = false;
};

const openResidentForm = () => {
    isNoDataOpen.value = false;
    isResidentOpen.value = true;
};

const closeResident = () => {
    isResidentOpen.value = false;
};

// После создания карточка уже находится по этому ИИН — повторяем поиск, чтобы
// подставить ФИО и контакты.
const onResidentCreated = async (createdIin) => {
    isResidentOpen.value = false;
    createdIins.add(String(createdIin));
    if (residentTarget.value === 'recipient') {
        await fetchRecipient(createdIin);
        return;
    }
    await fetchApplicant(createdIin);
};

// Карточка контрагента в КИАС ищется по ИИН — «Контрагент» документа нам не отдаётся.
// applicant: null означает, что карточки нет и её сначала нужно завести.
const fetchApplicant = async (value) => {
    if (!props.document) return;

    // Прежние данные принадлежат прежнему ИИН — убираем их до запроса, иначе при
    // смене ИИН в форме останется чужой заявитель.
    fullName.value = '';
    phone.value = '';
    email.value = '';

    isLookingUp.value = true;
    try {
        const res = await axios.get(
            `/V1/my-insurance-payments/${props.document.payment_id}/documents/${props.document.doc_isn}/form`,
            { params: { company_id: props.companyId, iin: value } },
        );

        const applicant = res.data.data?.applicant;

        if (!applicant) {
            promptResident('applicant', value);
            return;
        }

        fullName.value = [applicant.surname, applicant.name, applicant.patronymic].filter(Boolean).join(' ');
        if (applicant.phone) phone.value = formatPhone(applicant.phone);
        if (applicant.email) email.value = applicant.email;
    } catch {
        toaster.error('Не удалось получить данные контрагента', { position: 'top-right' });
    } finally {
        isLookingUp.value = false;
    }
};

const lookupApplicant = debounce((value) => fetchApplicant(value), 500);

// Выгодоприобретатель — тот же справочник контрагентов, просто другой ИИН. В ТЗ этого нет,
// но поле ровно такое же, и заставлять вводить ФИО руками нет причины.
const isLookingUpRecipient = ref(false);

const fetchRecipient = async (value) => {
    if (!props.document) return;

    recipientName.value = '';
    isLookingUpRecipient.value = true;
    try {
        const res = await axios.get(
            `/V1/my-insurance-payments/${props.document.payment_id}/documents/${props.document.doc_isn}/form`,
            { params: { company_id: props.companyId, iin: value } },
        );

        const person = res.data.data?.applicant;

        if (!person) {
            promptResident('recipient', value);
            return;
        }

        recipientName.value = [person.surname, person.name, person.patronymic].filter(Boolean).join(' ');
    } catch {
        toaster.error('Не удалось получить данные получателя', { position: 'top-right' });
    } finally {
        isLookingUpRecipient.value = false;
    }
};

const lookupRecipient = debounce((value) => fetchRecipient(value), 500);

watch(recipientIin, (value) => {
    if (String(value ?? '').length === 12) lookupRecipient(value);
});

// Следим за полем, а не за blur: иначе повторный ввод ИИН не обновлял заявителя.
watch(iin, (value) => {
    if (String(value ?? '').length === 12) lookupApplicant(value);
});

// КИАС отдаёт телефон как 87001234567 — приводим к маске поля.
const formatPhone = (raw) => {
    const digits = String(raw).replace(/\D/g, '').replace(/^8/, '7').padStart(11, '7').slice(0, 11);
    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
};

const onBeforeUpload = (file) => {
    files.value = [file];
    return false;
};

const onRemoveFile = (file) => {
    files.value = (files.value || []).filter(item => item.uid !== file.uid);
};

// Родитель закрывает окно по ответу сервера — здесь только отдаём значения.
const onSubmit = handleSubmit((values) => {
    emit('submit', { type: props.type, document: props.document, values });
});

const onCancel = () => emit('close');

watch(() => props.open, (isOpen) => {
    if (!isOpen) {
        isNoDataOpen.value = false;
        isResidentOpen.value = false;
        return;
    }

    resetForm({
        values: {
            country: props.prefill?.country ?? 'Казахстан',
            vehicle_mark_model: props.prefill?.vehicle?.mark_model ?? '',
            vehicle_plate_number: props.prefill?.vehicle?.plate_number ?? '',
            incident_date: props.prefill?.incident_date ?? '',
        },
    });

    if (isForm.value && hasRecipient.value) loadBanks();
});
</script>

<style lang="scss" scoped>
@mixin fontStyle ($family, $style, $weight, $size, $lheight, $color) {
    font-family: $family;
    font-style: normal;
    font-weight: $weight;
    font-size: $size;
    line-height: $lheight;
    color: $color;
}

.dm {
    &_header {
        margin-bottom: 20px;
    }

    &_title {
        @include fontStyle(Roboto, normal, 600, 22px, 30px, #1A1A1A);
        margin: 0 0 4px;
    }

    &_subtitle {
        @include fontStyle(Roboto, normal, 400, 13px, 20px, #909090);
        margin: 0;
    }

    &_section {
        border-top: 1px solid #F0F0F0;
        padding: 18px 0;
    }

    &_section_title {
        @include fontStyle(Roboto, normal, 400, 15px, 22px, #BFBFBF);
        margin: 0 0 16px;
    }

    &_grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4px 20px;
    }

    &_item {
        margin-bottom: 0;
    }

    &_alert {
        border: none;
        border-radius: 6px;
        margin-bottom: 20px;

        &_text {
            @include fontStyle(Roboto, normal, 400, 13px, 18px, #007BFF);
        }
    }

    &_dragger {
        &_icon {
            font-size: 40px;
            color: #007BFF;
            margin: 0 0 8px;
        }

        &_title {
            @include fontStyle(Roboto, normal, 500, 15px, 22px, #1A1A1A);
            margin: 0 0 4px;
        }

        &_hint {
            @include fontStyle(Roboto, normal, 400, 13px, 18px, #909090);
            margin: 0;
        }
    }

    &_footer {
        display: flex;
        gap: 12px;
        margin-top: 8px;
    }

    &_btn {
        flex: 1;
        height: 48px;

        // With :href ant renders an <a>, which doesn't centre its label like <button> does.
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
}

::v-deep(.dm_item) {
    .ant-form-item-label > label {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #373737);
    }

    .ant-input,
    .ant-select-selector {
        height: 44px;
        background: #F4F4F8;
        border: none;
        border-radius: 8px;
    }

    .ant-input,
    .ant-select-selection-item,
    .ant-select-selection-placeholder {
        @include fontStyle(Roboto, normal, 400, 14px, 44px, #1A1A1A);
    }

    .ant-select-selection-placeholder,
    .ant-input::placeholder {
        color: #909090;
    }

    .error,
    .error .ant-select-selector {
        box-shadow: inset 0 0 0 1px #FF4D4F;
    }
}
</style>
